import { useState } from 'react';
import { Link } from 'react-router-dom';

const SAMPLE_QUERY =
  'Show me every Originator asset in Line A that is pledged and also has a pending release or clarification';

const RESULT_ROWS = [
  {
    assetId: 'A-18802',
    status: 'Pledged',
    detail: 'Originator clarification',
    statusTone: 'gold',
  },
  {
    assetId: 'A-18797',
    status: 'Release pending',
    detail: 'Ready to execute',
    statusTone: 'teal',
  },
  {
    assetId: 'A-18697',
    status: 'Pledged',
    detail: 'Release pending (dual path)',
    statusTone: 'gold',
  },
  {
    assetId: 'A-18765',
    status: 'Pledged',
    detail: 'Provider clarification',
    statusTone: 'gold',
  },
];

function statusClass(tone) {
  return tone === 'gold'
    ? 'font-medium text-evault-gold'
    : 'font-medium text-evault-teal-deep';
}

const btnAsk =
  'rounded-full bg-evault-teal-deep px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-evault-teal-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-teal/40 focus-visible:ring-offset-2 sm:text-sm';

const btnSaved =
  'rounded-full bg-evault-navy px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-evault-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-navy/40 focus-visible:ring-offset-2 sm:text-sm';

const btnOpen =
  'inline-flex rounded-full bg-evault-teal-deep px-3 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-evault-teal-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-teal/30';

export function CustodianSearchAskPage() {
  const [query, setQuery] = useState(SAMPLE_QUERY);

  return (
    <div className="page-stack">
      <header className="border-b border-evault-border pb-4">
        <h1 className="text-xl font-semibold text-evault-navy">Search & ask</h1>
        <p className="mt-1 max-w-3xl text-xs text-evault-muted sm:text-sm">
          Grounded search and Q&amp;A over assets, evidence, receipts, and
          custody actions.
        </p>
      </header>

      <section className="card-shell p-4 sm:p-5">
        <h2 className="text-sm font-semibold text-umb-neutral-dark">
          Ask IDA
        </h2>
        <p className="mt-1 text-xs text-evault-muted">
          Natural-language query — answers cite receipts, bundles, and custody
          events.
        </p>
        <form
          className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-stretch"
          onSubmit={(e) => e.preventDefault()}
        >
          <label className="min-w-0 flex-1">
            <span className="sr-only">Question</span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-evault border border-evault-border bg-white px-4 py-3 text-sm text-umb-neutral-dark shadow-sm placeholder:text-evault-muted focus:border-evault-teal focus:outline-none focus:ring-2 focus:ring-evault-teal/20"
              placeholder="Ask about assets, pledges, releases, evidence…"
              aria-label="Ask IDA question"
            />
          </label>
          <div className="flex shrink-0 flex-wrap gap-2 lg:flex-col xl:flex-row">
            <button type="submit" className={btnAsk}>
              Ask
            </button>
            <button type="button" className={btnSaved}>
              Saved prompts
            </button>
          </div>
        </form>
      </section>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(240px,1fr)] lg:items-start">
        <section className="card-shell flex flex-col gap-4 p-4 sm:p-5">
          <div>
            <h2 className="text-sm font-semibold text-umb-neutral-dark">
              Answer + filters
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-umb-neutral-dark/90">
              I found 19 assets. 14 are in active pledge state and 5 are in
              release pending. 3 also have open clarification requests from the
              provider. The oldest unresolved item is{' '}
              <Link
                to="/custodian/assets/A-18697"
                className="font-semibold text-evault-teal-deep hover:underline"
              >
                A-18697
              </Link>
              .
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              Active filters
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex rounded-full bg-evault-gold/15 px-3 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-evault-gold/40">
                Pledged
              </span>
              <span className="inline-flex rounded-full bg-evault-teal-deep/10 px-3 py-1 text-[11px] font-semibold text-evault-teal-deep ring-1 ring-evault-teal-deep/25">
                Release pending
              </span>
              <span className="inline-flex rounded-full bg-evault-teal-deep/10 px-3 py-1 text-[11px] font-semibold text-evault-teal-deep ring-1 ring-evault-teal-deep/25">
                Originator
              </span>
            </div>
          </div>
          <div className="table-shell overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-[11px] sm:text-xs">
              <thead className="bg-evault-surface text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                <tr>
                  <th className="px-3 py-2">Asset</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sub-status</th>
                  <th className="px-3 py-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {RESULT_ROWS.map((row) => (
                  <tr
                    key={row.assetId}
                    className="border-t border-evault-border hover:bg-evault-surface/50"
                  >
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/custodian/assets/${row.assetId}`}
                        className="font-semibold text-evault-teal-deep hover:underline"
                      >
                        {row.assetId}
                      </Link>
                    </td>
                    <td className={`px-3 py-2.5 ${statusClass(row.statusTone)}`}>
                      {row.status}
                    </td>
                    <td className="px-3 py-2.5 text-umb-neutral-dark/85">
                      {row.detail}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link
                        to={`/custodian/assets/${row.assetId}`}
                        className={btnOpen}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="card-shell flex flex-col gap-4 p-4 sm:p-5">
          <div>
            <h2 className="text-sm font-semibold text-umb-neutral-dark">
              Grounding
            </h2>
            <p className="mt-1 text-xs text-evault-muted">
              Scope and source references for this answer.
            </p>
          </div>
          <ul className="list-disc space-y-1.5 pl-4 text-xs text-umb-neutral-dark/90">
            <li>19 assets matched</li>
            <li>19 custody receipts</li>
            <li>11 pledge receipts</li>
            <li>5 release requests</li>
            <li>3 clarification threads</li>
          </ul>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              References
            </p>
            <ul className="mt-2 space-y-2 text-xs">
              <li>
                <Link
                  to="/custodian/pledges"
                  className="font-medium text-evault-teal-deep hover:underline"
                >
                  Pledge receipt PL-11807
                </Link>
              </li>
              <li>
                <Link
                  to="/custodian/release-copy"
                  className="font-medium text-evault-teal-deep hover:underline"
                >
                  Release request RL-9901
                </Link>
              </li>
              <li>
                <Link
                  to="/custodian/exceptions"
                  className="font-medium text-evault-teal-deep hover:underline"
                >
                  Clarification thread CL-118
                </Link>
              </li>
              <li>
                <Link
                  to="/custodian/evidence/bundles"
                  className="font-medium text-evault-teal-deep hover:underline"
                >
                  Evidence bundle EB-18788
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
